/**
 * BikeComparison page
 *
 * Receives baseSessionId + source via location.state (set by WorkoutAnalysisV2).
 * Calls POST /api/compare, shows loading skeleton, renders comparison result.
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Alert, CircularProgress, Button, Skeleton, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { tokens } from '../theme';
import { compareBikeWorkout } from '../api/bikeComparisonApi';
import BikeComparisonHeader from '../components/bike/comparison/BikeComparisonHeader';
import BikeComparisonView from '../components/BikeComparisonView';

function LoadingSkeleton({ onCancel }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box m="20px">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width={200} height={36} sx={{ bgcolor: colors.grey[800] }} />
        <Button variant="text" onClick={onCancel} sx={{ color: colors.grey[400] }}>Cancel</Button>
      </Box>
      <Box display="flex" gap={2} mb={3}>
        <Skeleton variant="rectangular" width="45%" height={120} sx={{ borderRadius: 2, bgcolor: colors.grey[800] }} />
        <Box display="flex" alignItems="center">
          <Typography variant="h4" color={colors.grey[500]}>vs</Typography>
        </Box>
        <Skeleton variant="rectangular" width="45%" height={120} sx={{ borderRadius: 2, bgcolor: colors.grey[800] }} />
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography color={colors.grey[400]}>Analyzing comparison workout…</Typography>
      </Box>
    </Box>
  );
}

export default function BikeComparison() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();

  const { baseSessionId, source, aFilename } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [swapped, setSwapped] = useState(false);

  useEffect(() => {
    if (!baseSessionId || !source) {
      navigate('/analysis');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    compareBikeWorkout(baseSessionId, source)
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || err.message || 'Comparison failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [baseSessionId, source, navigate]);

  const handleSwap = () => setSwapped((s) => !s);

  const displayedResult = result && swapped
    ? {
        ...result,
        analysis_a: result.analysis_b,
        analysis_b: result.analysis_a,
        a_filename: result.b_filename,
        b_filename: result.a_filename,
        alignment: result.alignment
          ? {
              ...result.alignment,
              resampled: result.alignment.resampled?.map(r => ({
                ...r,
                time_delta_s: -(r.time_delta_s ?? 0),
                elapsed_a_s: r.elapsed_b_s,
                elapsed_b_s: r.elapsed_a_s,
                speed_a: r.speed_b,
                speed_b: r.speed_a,
                power_a: r.power_b,
                power_b: r.power_a,
                gradient_a: r.gradient_b,
                gradient_b: r.gradient_a,
                altitude_a: r.altitude_b,
                altitude_b: r.altitude_a,
              })),
            }
          : null,
      }
    : result;

  if (loading) return <LoadingSkeleton onCancel={() => navigate(-1)} />;

  if (error) {
    return (
      <Box m="20px">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</Button>
      </Box>
    );
  }

  if (!result) return null;

  return (
    <Box m="20px">
      <Box mb={2} display="flex" alignItems="center" gap={1}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ color: colors.grey[400] }}
        >
          Back
        </Button>
        <Typography variant="h5" color={colors.grey[100]}>Bike Comparison</Typography>
      </Box>

      <BikeComparisonHeader
        analysisA={displayedResult?.analysis_a}
        analysisB={displayedResult?.analysis_b}
        alignment={displayedResult?.alignment}
        aFilename={displayedResult?.a_filename || aFilename}
        bFilename={displayedResult?.b_filename}
        onSwap={handleSwap}
      />

      <Box mt={3}>
        <BikeComparisonView result={displayedResult} />
      </Box>
    </Box>
  );
}
